import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, FileText, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export const AdminOrders = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    const qs = filter && filter !== 'all' ? `?status=${filter}` : '';
    const res = await api.get(`/admin/orders${qs}`);
    setOrders(res.data);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate, filter]);

  const updateOrder = async (id, patch) => {
    try {
      const res = await api.put(`/admin/orders/${id}`, patch);
      if (res.data.invoice_generated) toast.success('Tax invoice auto-generated');
      else toast.success('Order updated');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const generateInvoice = async (id, force = false) => {
    setBusyId(id);
    try {
      const res = await api.post(`/admin/orders/${id}/generate-invoice${force ? '?force=true' : ''}`);
      toast.success(`Invoice ${res.data.invoice_number} ${force ? 'regenerated' : 'created'}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setBusyId(null);
    }
  };

  const downloadInvoice = async (id, num) => {
    try {
      const res = await api.get(`/orders/${id}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${num}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const statusColor = (s) => s === 'delivered' ? 'bg-green-100 text-green-700' : s === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700';
  const invoiceEligible = (o) =>
    o.payment_status === 'paid' || (o.payment_method === 'cod' && o.order_status === 'delivered');

  if (authLoading || !user || user.role !== 'admin') return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin')}><ArrowLeft size={24} /></button>
          <h1 className="text-2xl font-bold">Orders</h1>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div></div> : (
        <div className="space-y-4">
          {orders.map(o => (
            <div key={o.id} className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between mb-4">
                <div><p className="font-bold">{o.order_number}</p><p className="text-sm text-gray-500">{new Date(o.created_at).toLocaleString()}</p></div>
                <div className="flex gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm ${statusColor(o.order_status)}`}>{o.order_status}</span>
                  <span className={`px-3 py-1 rounded-full text-sm ${o.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{o.payment_status}</span>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
                <div><p className="text-gray-500">Customer</p><p>{o.user_name}</p><p>{o.user_phone}</p></div>
                <div><p className="text-gray-500">Address</p><p>{o.shipping_address}, {o.shipping_city} - {o.shipping_pincode}</p></div>
              </div>
              <div className="border-t pt-4 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                <div>
                  <p className="font-bold text-lg">₹{o.total}</p>
                  {o.invoice_number && (
                    <p className="text-xs text-gray-500 mt-1">
                      <FileText size={12} className="inline mr-1" />
                      {o.invoice_number}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={o.payment_status} onValueChange={(v) => updateOrder(o.id, { payment_status: v })}>
                    <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Payment Pending</SelectItem>
                      <SelectItem value="pending_verification">Pending Verification</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={o.order_status} onValueChange={(v) => updateOrder(o.id, { order_status: v })}>
                    <SelectTrigger className="w-32 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  {!o.invoice_number && invoiceEligible(o) && (
                    <Button
                      data-testid={`admin-generate-invoice-${o.id}`}
                      size="sm"
                      onClick={() => generateInvoice(o.id)}
                      disabled={busyId === o.id}
                      className="bg-orange-600 hover:bg-orange-700 h-9"
                    >
                      <FileText size={14} className="mr-1" /> Generate Invoice
                    </Button>
                  )}
                  {o.invoice_number && (
                    <>
                      <Button
                        data-testid={`admin-download-invoice-${o.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => downloadInvoice(o.id, o.order_number)}
                        className="border-orange-600 text-orange-700 h-9"
                      >
                        <Download size={14} className="mr-1" /> PDF
                      </Button>
                      <Button
                        data-testid={`admin-regenerate-invoice-${o.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => generateInvoice(o.id, true)}
                        disabled={busyId === o.id}
                        title="Regenerate (overwrites the existing PDF)"
                        className="text-gray-600 h-9"
                      >
                        <RefreshCw size={14} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {orders.length === 0 && <div className="text-center py-12 bg-white rounded-xl text-gray-500">No orders</div>}
        </div>
      )}
    </div>
  );
};
